import React, { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getAppContext } from '@/lib/app-context';

import Layout from './components/Layout';
import AdminLayout from './pages/admin/AdminLayout';
import LoginPage from './pages/login/LoginPage';
import NotFound from './pages/NotFound/NotFound';

/*
 * 业务页面按路由拆包，打开哪页才下载哪页。
 *
 * 原来全部静态引入，所有页面连同 ECharts、xlsx 打成一个 2.9MB 的包，
 * 服务器出口带宽又小，首屏光下这一个文件就要十几秒。
 * 外壳（Layout / AdminLayout / 登录页）仍然静态引入，保证先有东西可看。
 */
const StatsPage = lazy(() => import('./pages/admin/StatsPage'));
const RunManagePage = lazy(() => import('./pages/admin/RunManagePage'));
const AccountManagePage = lazy(() => import('./pages/admin/AccountManagePage'));
const SubscriptionManagePage = lazy(
  () => import('./pages/admin/SubscriptionManagePage'),
);
const ProxyIpPage = lazy(() => import('./pages/admin/ProxyIpPage'));
const PaymentCardPage = lazy(() => import('./pages/admin/PaymentCardPage'));
const SubscriptionPlanPage = lazy(
  () => import('./pages/admin/SubscriptionPlanPage'),
);
const OperationLogPage = lazy(() => import('./pages/admin/OperationLogPage'));
const AiApiKeyPage = lazy(() => import('./pages/admin/AiApiKeyPage'));
const MemberPage = lazy(() => import('./pages/admin/MemberPage'));
const MyAccountPage = lazy(() => import('./pages/account/MyAccountPage'));

// 首页按角色分流：管理者进管理后台，普通用户进我的订阅页
const HomeRoute: React.FC = () => {
  const { isAdmin, isLoading } = useAuth();
  if (isLoading) {
    return null;
  }
  return isAdmin ? (
    <Navigate to="/admin/stats" replace />
  ) : (
    <Navigate to="/my" replace />
  );
};

// 管理后台守卫：非管理者访问跳回我的订阅页
const AdminGuard: React.FC = () => {
  const { isAdmin, isLoading } = useAuth();
  if (isLoading) {
    return null;
  }
  return isAdmin ? <AdminLayout /> : <Navigate to="/my" replace />;
};

const RoutesComponent = () => {
  const { user, isLoading } = useAuth();

  /*
   * 未登录一律落到登录页。
   *
   * /api/auth/me 对未登录返回的是 200 加 authenticated:false，不是 401，
   * 所以 http.ts 里那个 401 拦截器不会触发；没有这道判断的话，
   * 未登录的人会看到一层空界面，直到某个业务接口回 401 才被动跳走。
   *
   * 等 /api/auth/me 回来这段时间也得有东西可看。服务端已经随 HTML 注入了
   * 登录态，未登录时直接先把登录页画出来，省掉一次白屏；已登录的话
   * 注入的上下文会让 isLoading 一开始就是 false，走不到这里。
   */
  if (isLoading) {
    return getAppContext().authenticated ? null : <LoginPage />;
  }
  if (user === null) {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomeRoute />} />
        <Route path="my" element={<MyAccountPage />} />
        <Route path="admin" element={<AdminGuard />}>
          <Route index element={<Navigate to="/admin/stats" replace />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="runs" element={<RunManagePage />} />
          {/* 额度监控并进了订阅管理，旧链接跳过去 */}
          <Route
            path="quota"
            element={<Navigate to="/admin/subscriptions" replace />}
          />
          <Route path="accounts" element={<AccountManagePage />} />
          <Route path="subscriptions" element={<SubscriptionManagePage />} />
          <Route path="proxies" element={<ProxyIpPage />} />
          <Route path="cards" element={<PaymentCardPage />} />
          <Route path="plans" element={<SubscriptionPlanPage />} />
          <Route path="members" element={<MemberPage />} />
          {/* 授权提取并进了订阅管理的「更多」菜单，旧链接跳过去 */}
          <Route
            path="auth-extract"
            element={<Navigate to="/admin/subscriptions" replace />}
          />
          <Route path="logs" element={<OperationLogPage />} />
          <Route path="ai-keys" element={<AiApiKeyPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default RoutesComponent;
